import { useCallback, useEffect, useState } from 'react'
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { actualizar, eliminar, enviar, mensajeError, obtener } from '../api/client'
import { alerta } from '../utils/alertas'
import { Modal } from '../components/Modal'
import type { RolDetalle, Sucursal, UsuarioListItem } from '../types'

interface FormularioUsuario {
  usuarioId: number
  usuario: string
  nombreCompleto: string
  email: string
  telefono: string
  rolId: number
  sucursalId: number | null
  password: string
  pin: string
  activo: boolean
  sucursales: number[]
}

const vacio: FormularioUsuario = {
  usuarioId: 0,
  usuario: '',
  nombreCompleto: '',
  email: '',
  telefono: '',
  rolId: 0,
  sucursalId: null,
  password: '',
  pin: '',
  activo: true,
  sucursales: []
}

const formatearFecha = (valor?: string | null) =>
  valor ? new Date(valor).toLocaleString('es-MX') : '-'

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState<UsuarioListItem[]>([])
  const [roles, setRoles] = useState<RolDetalle[]>([])
  const [sucursales, setSucursales] = useState<Sucursal[]>([])
  const [cargando, setCargando] = useState(true)
  const [modal, setModal] = useState(false)
  const [formulario, setFormulario] = useState<FormularioUsuario>(vacio)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const [u, r, s] = await Promise.all([
        obtener<UsuarioListItem[]>('usuarios'),
        obtener<RolDetalle[]>('roles'),
        obtener<Sucursal[]>('sucursales')
      ])
      setUsuarios(u)
      setRoles(r)
      setSucursales(s)
    } catch (error) {
      await alerta.error('No se pudieron cargar los usuarios', mensajeError(error))
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const abrirNuevo = () => {
    setFormulario({ ...vacio, rolId: roles[0]?.rolId ?? 0, sucursalId: sucursales[0]?.sucursalId ?? null })
    setModal(true)
  }

  const abrirEdicion = (u: UsuarioListItem) => {
    setFormulario({
      usuarioId: u.usuarioId,
      usuario: u.usuario,
      nombreCompleto: u.nombreCompleto,
      email: u.email ?? '',
      telefono: u.telefono ?? '',
      rolId: u.rolId,
      sucursalId: u.sucursalId ?? null,
      password: '',
      pin: '',
      activo: u.activo,
      sucursales: []
    })
    setModal(true)
  }

  const guardar = async () => {
    try {
      const cuerpo = {
        ...formulario,
        tenantId: 1,
        sucursales: formulario.sucursales.length ? formulario.sucursales : null
      }
      if (formulario.usuarioId > 0) await actualizar(`usuarios/${formulario.usuarioId}`, cuerpo)
      else await enviar('usuarios', cuerpo)
      setModal(false)
      await cargar()
      void alerta.toast('Usuario guardado')
    } catch (error) {
      await alerta.error('No se pudo guardar el usuario', mensajeError(error))
    }
  }

  const borrar = async (u: UsuarioListItem) => {
    if (!(await alerta.eliminar('Desactivar usuario', `${u.nombreCompleto} ya no podra iniciar sesion.`))) return
    try {
      await eliminar(`usuarios/${u.usuarioId}`)
      await cargar()
      void alerta.toast('Usuario desactivado')
    } catch (error) {
      await alerta.error('No se pudo desactivar', mensajeError(error))
    }
  }

  return (
    <div className="anima-entrada">
      <div className="fila-entre mb-4">
        <div>
          <h2 className="m-0 text-xl font-bold">Usuarios</h2>
          <p className="texto-suave mt-1">Personal con acceso al sistema. El PIN permite el acceso rapido desde la Logística.</p>
        </div>
        <div className="fila mr-15">
          <button className="btn btn-plano btn-icono" onClick={() => void cargar()}>
            <RefreshCw size={17} className={cargando ? 'anima-pulso' : ''} />
          </button>
          <button className="btn" onClick={abrirNuevo}>
            <Plus size={17} /> Nuevo 
          </button>
        </div>
      </div>

      <div className="tabla-contenedor">
        <table>
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Nombre</th>
              <th>Rol</th>
              <th>Sucursal</th>
              <th>Ultimo acceso</th>
              <th>Activo</th>
              <th className="w-28">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.usuarioId}>
                <td>{u.usuario}</td>
                <td>{u.nombreCompleto}</td>
                <td>{u.rol}</td>
                <td>{u.sucursal ?? '-'}</td>
                <td>{formatearFecha(u.ultimoAcceso)}</td>
                <td>
                  <span className={u.activo ? 'etiqueta etiqueta-ok' : 'etiqueta etiqueta-danger'}>{u.activo ? 'Si' : 'No'}</span>
                </td>
                <td>
                  <div className="fila gap-1.5">
                    <button className="btn btn-secundario btn-sm" onClick={() => abrirEdicion(u)}>
                      <Pencil size={14} />
                    </button>
                    <button className="btn btn-peligro btn-sm" onClick={() => void borrar(u)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        abierto={modal}
        titulo={formulario.usuarioId > 0 ? 'Editar usuario' : 'Nuevo usuario'}
        onCerrar={() => setModal(false)}
        pie={
          <>
            <button className="btn btn-plano" onClick={() => setModal(false)}>Cancelar</button>
            <button className="btn" onClick={() => void guardar()}>Guardar</button>
          </>
        }
      >
        <div className="grid-2">
          <div className="campo">
            <label htmlFor="usuario">Usuario</label>
            <input id="usuario" value={formulario.usuario} onChange={(e) => setFormulario({ ...formulario, usuario: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="nombre">Nombre completo</label>
            <input id="nombre" value={formulario.nombreCompleto} onChange={(e) => setFormulario({ ...formulario, nombreCompleto: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="email">Correo</label>
            <input id="email" value={formulario.email} onChange={(e) => setFormulario({ ...formulario, email: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="telefono">Telefono</label>
            <input id="telefono" value={formulario.telefono} onChange={(e) => setFormulario({ ...formulario, telefono: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="rol">Rol</label>
            <select id="rol" value={formulario.rolId} onChange={(e) => setFormulario({ ...formulario, rolId: Number(e.target.value) })}>
              {roles.map((r) => (
                <option key={r.rolId} value={r.rolId}>{r.nombre}</option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="sucursal">Sucursal principal</label>
            <select
              id="sucursal"
              value={formulario.sucursalId ?? ''}
              onChange={(e) => setFormulario({ ...formulario, sucursalId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">Sin asignar</option>
              {sucursales.map((s) => (
                <option key={s.sucursalId} value={s.sucursalId}>{s.nombre}</option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="password">Contrasena {formulario.usuarioId > 0 && '(dejar vacio para conservar)'}</label>
            <input id="password" type="password" value={formulario.password} onChange={(e) => setFormulario({ ...formulario, password: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="pin">PIN de acceso rapido</label>
            <input id="pin" value={formulario.pin} onChange={(e) => setFormulario({ ...formulario, pin: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="activo">Activo</label>
            <input id="activo" type="checkbox" checked={formulario.activo} onChange={(e) => setFormulario({ ...formulario, activo: e.target.checked })} className="h-5 w-5 accent-tema" />
          </div>
        </div>
      </Modal>
    </div>
  )
}
